import React from "react";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { enqueueSnackbar } from "notistack";
import { useForm, Controller, useFormState } from "react-hook-form";
import { useAnimationControls } from "framer-motion";
import AnalyticsContext from "@/utils/analytics-provider";
import CompanyDetails from "@/components/inquiry/company-details";
import QuoteItems from "@/components/inquiry/quote-items";
import InquiryContext from "@/utils/inquiry-context";
import InquirySuccessDialog from "@/components/inquiry/inquiry-success";
import Spinner from "@/components/common/spinner";
import { DevTool } from "@hookform/devtools";

const translationNamespaces = ["common", "nav"];

export const getStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, translationNamespaces)),
    },
  };
};

const Inquiry = () => {
  // const { t } = useTranslation("common");
  const analytics = React.useContext(AnalyticsContext);
  const router = useRouter();
  const { products } = React.useContext(InquiryContext);

  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const form = useForm({
    defaultValues: {
      fullName: "",
      companyName: "",
      email: "",
      phone: "",
      businessType: [],
      areaOfOperation: [],
      incoterm: null,
      port: "",
      orderVolume: "Few Tons",
      products: [],
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    control,
    formState: { errors },
  } = form;

  const clearForm = () => {
    form.reset();
    setSent(false);
    setPhoneOverride(false);
  };

  const handleSend = async (data) => {
    analytics.action("contact-form-submitted");
    setSending(true);
    const appendedData = {
      ...data,
      products,
    };
    console.log(appendedData);
    try {
      const response = await fetch("/api/inquire", {
        method: "POST",
        body: JSON.stringify(appendedData),
      });
      setSending(false);
      if (response.ok) {
        setSent(true);
        analytics.success("contact-form-submitted");
      } else {
        analytics.error("contact-form-submitted", response.statusText);
        enqueueSnackbar(
          "An error occured while sending your message. Please try again.",
          { variant: "error" },
        );
      }
    } catch (err) {
      console.log(err);
      analytics.error("contact-form-submitted", err);
      enqueueSnackbar(
        "An error occured while sending your message. Please try again later.",
        { variant: "error" },
      );
      setSending(false);
    }
  };

  const controls = useAnimationControls();

  // Declare a motion variant to shake the element along the x axis 3 times.
  const shake = {
    x: [0, -3, 3, -3, 3, -3, 3, 0],
    transition: { duration: 0.3 },
  };

  const { isSubmitting } = useFormState({ control });
  // Animate the element when the error changes.
  React.useEffect(() => {
    if (
      !isSubmitting &&
      (errors.fullName ||
        errors.companyName ||
        errors.email ||
        errors.phone ||
        errors.incoterms ||
        errors.port)
    ) {
      controls.start(shake);
    }
  }, [isSubmitting]);

  return (
    <form
      id="inquiry-form"
      className="bg-auto-white"
      noValidate
      onSubmit={handleSubmit(handleSend)}
    >
      <NextSeo
        title="Request a Price Quote"
        description="Request a price quote from AlHayat Foodstuff."
        canonical="https://alhayatfoodstuff.com/inquiry"
        openGraph={{
          title: "Request a Price Quote",
          description: "Request a price quote from AlHayat Foodstuff.",
        }}
      />
      <section className="bg-auto-black/10">
        <div className="container flex gap-8 px-4 py-10 sm:px-8">
          <button onClick={() => router.back()} type="button">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-6 w-6 sm:h-11 sm:w-11"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>
          </button>
          <h1 className="p-0 text-2xl font-bold text-primary-800 sm:text-4xl md:text-5xl">
            Request Price Quote
          </h1>
        </div>
      </section>
      <section className="bg-auto-black/5 sm:pt-4">
        <div className="container flex flex-col gap-4 p-4 sm:bg-transparent sm:p-8">
          <CompanyDetails form={form} controls={controls} />
        </div>
        <div className="container flex flex-col gap-4 p-4 sm:px-8">
          <QuoteItems />
        </div>
        <div className="container flex flex-row items-center justify-end gap-4 p-4 pb-16 text-end sm:px-8">
          <button
            className="button w-full sm:w-64"
            type="submit"
            disabled={sending}
          >
            {sending ? (
              <Spinner className="h-6 w-6 fill-white/80 text-white/40" />
            ) : (
              "Send"
            )}
          </button>
        </div>
      </section>
      <InquirySuccessDialog open={sent} />
      <DevTool control={control} />
    </form>
  );
};

export default Inquiry;
